import styles from "./CensusTable.module.css";
import rowStyles from "./CensusRow.module.css";
import { Button, ActionFooter, Alert } from "@justworkshr/milo-components";
import CensusRow from "./CensusRow";
import { EmployeeType, DependentType } from "./types";
import {
  trackCensusTableSubmission,
  trackCensusTableView,
} from "utils/analytics/trackEvent";
import {
  BACK_BUTTON_TEXT,
  NEXT_BUTTON_TEXT,
} from "pages/CensusAndCurrentInformation/utils/constants";
import { useEffect, useState } from "react";
import { hasQueryParam } from "utils/helper";

interface EmployeeTableFormProps {
  handleBackClick?: () => void;
  handleDelete: (uuid: string, participantType?: string) => void;
  isCobra?: boolean;
  isLoading?: boolean;
  offerHealthInsurance?: boolean;
  openDrawer: (initialData?: EmployeeType | DependentType) => () => void;
  censusList: EmployeeType[];
  handleSaveAndContinue: () => void;
  nextButtonText?: string;
}

const EmployeeTable = ({
  handleBackClick,
  handleDelete,
  isCobra,
  // offerHealthInsurance,
  openDrawer,
  censusList,
  isLoading,
  handleSaveAndContinue,
  nextButtonText,
}: EmployeeTableFormProps): JSX.Element => {
  const [isShowAlertOpen, setisShowAlertOpen] = useState(false);
  const [alertContent, setAlertContent] = useState("");

  const v2_dedupe_census_flow = hasQueryParam("usecensustab") && !isCobra;

  const MINIMUM_EMPLOYEE_NUMBER = !isCobra ? 2 : 1;

  useEffect(() => {
    trackCensusTableView({
      drawerVariant: "text-button",
      isCobra: isCobra || false,
    });
  }, [isCobra]);

  const flattenCensusList = (list: EmployeeType[]) => {
    return list.reduce((acc: (EmployeeType | DependentType)[], employee) => {
      if (isCobra) {
        let participantType = employee.participantType?.toLowerCase() || "";
        participantType =
          participantType[0].toUpperCase() + participantType.slice(1);
        employee.participantType = participantType;
      }

      if (
        (v2_dedupe_census_flow &&
          isCobra &&
          employee.participantType !== "Employee") ||
        (!v2_dedupe_census_flow &&
          ((isCobra && employee.participantType !== "Employee") ||
            (!isCobra && employee.source !== "WW_HI_APP")))
      ) {
        return acc;
      }

      acc.push({
        ...employee,
        participantType: employee.isCompanyOwner ? "Owner" : "Employee",
      });

      if (employee.dependentCensuses) {
        employee.dependentCensuses.forEach((dependent) => {
          acc.push(dependent);
        });
      }

      if (employee.cobraDependents) {
        employee.cobraDependents.forEach((dependent) => {
          let dependentType = dependent.participantType?.toLowerCase() || "";
          dependentType =
            dependentType[0].toUpperCase() + dependentType.slice(1);
          dependent.participantType = dependentType;
          dependent.employeeCensusId = employee.uuid;
          acc.push(dependent);
        });
      }
      return acc;
    }, []);
  };

  const healthCensusList = flattenCensusList(censusList);

  function validateAllMembers(healthCensusList: EmployeeType[]): boolean {
    for (const employee of healthCensusList) {
      if (
        !employee.firstName ||
        !employee.lastName ||
        !employee.participantType ||
        !employee.dateOfBirth ||
        !employee.gender ||
        !employee.homeState ||
        !employee.homeZip
      ) {
        return false;
      }
    }
    return true;
  }

  const handleNextClick = () => {
    const hasMinimumEmployees =
      (healthCensusList?.filter(
        (emp) =>
          emp.participantType === "Employee" || emp.participantType === "Owner"
      )?.length ?? 0) >= MINIMUM_EMPLOYEE_NUMBER;
    const allRequiredFieldsFilled = validateAllMembers(healthCensusList);

    const showAlert = v2_dedupe_census_flow
      ? !allRequiredFieldsFilled
      : !hasMinimumEmployees;

    if (showAlert) {
      let alertMessage = "";
      if (!allRequiredFieldsFilled && v2_dedupe_census_flow) {
        alertMessage =
          "To continue, please provide all required information for everyone listed";
      } else {
        alertMessage = `Be sure to add ${
          isCobra ? "your COBRA participants" : "at least 2 teammates"
        } to continue`;
      }

      setAlertContent(alertMessage);
      !isShowAlertOpen && setisShowAlertOpen(true);
    } else {
      const employeeCount = healthCensusList?.filter(
        (emp) =>
          emp.participantType === "Employee" || emp.participantType === "Owner"
      ).length;
      const dependentCount = healthCensusList.length - employeeCount;

      trackCensusTableSubmission({
        drawerVariant: "text-button",
        dependentCount,
        employeeCount,
        isCobra: isCobra || false,
      });

      setisShowAlertOpen(false);
      handleSaveAndContinue();
    }
  };

  useEffect(() => {
    if (healthCensusList) {
      const hideMinimumReqAlert =
        (healthCensusList?.filter(
          (emp) =>
            emp.participantType === "Employee" ||
            emp.participantType === "Owner"
        )?.length ?? 0) >= MINIMUM_EMPLOYEE_NUMBER;

      const hideCompleteInfoAlert = validateAllMembers(healthCensusList);

      const shouldCloseAlert =
        (v2_dedupe_census_flow && hideCompleteInfoAlert) ||
        (!v2_dedupe_census_flow && hideMinimumReqAlert);

      if (shouldCloseAlert) {
        setisShowAlertOpen(false);
      }
      return;
    }
  }, [healthCensusList, v2_dedupe_census_flow, MINIMUM_EMPLOYEE_NUMBER]);

  return (
    <section className={styles.employeeTable}>
      <div className={styles.buttonContainer}>
        <h3>Team + dependents ({healthCensusList?.length || 0})</h3>
        {!v2_dedupe_census_flow && (
          <Button
            onClick={openDrawer()}
            color="neutral"
            leftIcon="plus"
            size="sm"
            className={styles.addTeammateButton}
          >
            New teammate
          </Button>
        )}
      </div>
      <div className={styles.censusListContainer}>
        <div className={styles.censusListScrollShadow} />
        <div className={styles.censusListScrollContainer}>
          <ul className={styles.censusList}>
            <li
              className={`${rowStyles.censusListHeader} ${
                rowStyles.alternateRow
              } ${v2_dedupe_census_flow ? rowStyles.sixColumns : ""}`}
            >
              <div
                className={`${rowStyles.nameCell} ${rowStyles.censusListCell}`}
              >
                Name
              </div>
              <div className={rowStyles.censusListCell}>Type</div>
              <div className={rowStyles.censusListCell}>Date of birth</div>
              {v2_dedupe_census_flow && (
                <div className={rowStyles.censusListCell}>Sex</div>
              )}
              <div className={rowStyles.censusListCell}>Zip</div>
            </li>
            {healthCensusList.length !== 0 &&
              healthCensusList.map((employee, index) => (
                <>
                  <CensusRow
                    key={index}
                    employee={employee}
                    openDrawer={openDrawer}
                    handleDelete={handleDelete}
                    isCobra={isCobra}
                  />
                </>
              ))}
            {!healthCensusList.length && (
              <div className={rowStyles.emptyCensusDiv}>
                <div className={rowStyles.emptyCensusDivTitle}>
                  You haven’t added anyone yet
                </div>
                <div className={rowStyles.emptyCensusDivDescription}>
                  {isCobra
                    ? "Some carriers have restrictions based on the percentage of applicants on COBRA."
                    : "You need at least 2 eligible employees from different households to enroll."}
                </div>
              </div>
            )}
          </ul>
        </div>
      </div>

      {isShowAlertOpen && (
        <Alert className={styles.alert} color="warning" visible={true}>
          {alertContent}
        </Alert>
      )}

      <ActionFooter
        className={styles.footerContainer}
        actions={[
          <Button variant="ghost" onClick={handleBackClick}>
            {BACK_BUTTON_TEXT}
          </Button>,
          <Button disabled={isLoading} onClick={handleNextClick}>
            {nextButtonText || NEXT_BUTTON_TEXT}
          </Button>,
        ]}
      />
    </section>
  );
};

export default EmployeeTable;
